import { useRef, useState } from "react";
import { ingestionApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";

// Phase B per agent-detail-viewer-v1.md §10 row 13. Upload sub-tab.
// Three sources, all reusing existing /api/ingestion endpoints:
//   files     → /api/ingestion/upload  (drag-drop multi-file)
//   url       → /api/ingestion/crawl   (web scrape)
//   text      → /api/ingestion/text    (paste raw text)

const Card = ({ title, children, footer }) => (
  <div style={{
    padding: 16, borderRadius: 12,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    boxShadow: MATERIALS.spatialShadow(),
    display: "flex", flexDirection: "column", gap: 10,
  }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: COLORS.textDim,
      textTransform: "uppercase", letterSpacing: "0.06em",
    }}>{title}</div>
    {children}
    {footer && (
      <div style={{ fontSize: 10, color: COLORS.textDim, borderTop: `1px solid ${COLORS.border}`, paddingTop: 8 }}>
        {footer}
      </div>
    )}
  </div>
);

const Row = ({ children }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{children}</div>
);

const Field = ({ label, children, hint }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
    <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
    {children}
    {hint && <span style={{ fontSize: 10, color: COLORS.textDim }}>{hint}</span>}
  </div>
);

const inputStyle = {
  padding: "6px 10px", borderRadius: 8,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  color: COLORS.text, fontSize: 12, outline: "none",
};

const btnStyle = (color = COLORS.accent, disabled = false) => ({
  padding: "8px 16px", borderRadius: 8, border: "none",
  background: disabled ? COLORS.surfaceHover : color,
  color: disabled ? COLORS.textDim : "#fff",
  fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
});

const Banner = ({ kind, children, onDismiss }) => {
  const color = kind === "error" ? COLORS.pink : COLORS.green;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}55`,
      background: `${color}18`, color: COLORS.text, fontSize: 12,
      display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start",
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: "none", color: COLORS.textDim,
          cursor: "pointer", fontSize: 14,
        }}>{"✕"}</button>
      )}
    </div>
  );
};

export default function KnowledgeUploadPanel({ agent }) {
  const agentId = agent?.id;
  const [chunkSize, setChunkSize] = useState("");
  const [chunkOverlap, setChunkOverlap] = useState("");
  const [classifications, setClassifications] = useState("");

  const [busy, setBusy] = useState(null);   // "files" | "url" | "text" | null
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  // Files
  const fileInputRef = useRef(null);
  const [pickedFiles, setPickedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const onPick = (fileList) => {
    if (!fileList) return;
    setPickedFiles(Array.from(fileList));
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) onPick(e.dataTransfer.files);
  };

  // Above this threshold, route a single file through chunked upload
  // (/upload/init + /upload/chunk × N + /upload/complete) instead of
  // a single multipart POST. Each chunk is 10MB (see api/client.js).
  // Without chunked upload, a 500MB single POST holds the entire body
  // in the API pod's RAM — degrading every concurrent user on that pod.
  // Pre-f953f3f (2026-04-29) the old IngestionView did this routing;
  // the migration lost it. Files 100MB–1GB now stream in chunks again;
  // files >1GB stay blocked by the 1GB backend cap.
  const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // 100 MB

  // Detect archive files. The /upload endpoint parses single documents;
  // archives must use /bulk-upload which extracts members and dispatches
  // each through the engine. The backend (Commit 1) also enforces this
  // with a 415 guard; this frontend route is the primary UX path.
  // Pre-f953f3f (2026-04-29) the old IngestionView had this detection;
  // the migration lost it and zips silently failed for 40+ days.
  const zipFile = pickedFiles.find(f => f.name.toLowerCase().endsWith(".zip"));
  const hasMixedZipAndOther = !!zipFile && pickedFiles.length > 1;
  const largeFile = pickedFiles.find(f => f.size > CHUNKED_THRESHOLD);
  const hasLargeFileInBatch = !!largeFile && pickedFiles.length > 1;

  const submitFiles = async () => {
    if (!pickedFiles.length || !agentId) return;
    if (hasMixedZipAndOther) {
      setErr("Upload zip files one at a time. Remove other files from this batch, or upload them separately.");
      return;
    }
    if (hasLargeFileInBatch) {
      setErr(`Upload large files (>${(CHUNKED_THRESHOLD / 1024 / 1024).toFixed(0)} MB) one at a time. ${largeFile.name} is ${(largeFile.size / 1024 / 1024).toFixed(0)} MB.`);
      return;
    }
    setBusy("files"); setErr(null); setResult(null);
    try {
      const opts = {};
      if (chunkSize) opts.chunk_size = Number(chunkSize);
      if (chunkOverlap) opts.chunk_overlap = Number(chunkOverlap);
      if (classifications) opts.classifications = classifications;

      let res;
      let message;
      if (zipFile) {
        // Zip → /bulk-upload. Returns {job_id, file_count}.
        res = await ingestionApi.bulkUpload(agentId, zipFile, opts);
        const data = res?.data || res;
        const fileCount = data?.file_count ?? "?";
        message = `Bulk ingest started: ${zipFile.name} (${fileCount} files extracted). Job: ${data?.job_id || "—"}`;
      } else if (largeFile) {
        // Single large file → chunked upload. Show per-chunk progress
        // so the user knows we haven't stalled on a multi-minute POST.
        const sizeMB = (largeFile.size / 1024 / 1024).toFixed(0);
        setResult({ kind: "files", message: `Uploading ${largeFile.name} (${sizeMB} MB) in chunks…` });
        res = await ingestionApi.chunkedUpload(agentId, largeFile, {
          ...opts,
          onProgress: ({ chunk, total, percent }) => {
            setResult({
              kind: "files",
              message: `Uploading ${largeFile.name}: chunk ${chunk}/${total} (${percent}%)`,
            });
          },
        });
        const data = res?.data || res;
        message = `Uploaded ${largeFile.name} (${sizeMB} MB). Job: ${data?.job_id || "—"}`;
      } else {
        res = await ingestionApi.uploadPipeline(agentId, pickedFiles, opts);
        message = `Uploaded ${pickedFiles.length} file${pickedFiles.length > 1 ? "s" : ""}. Job: ${res?.job_id || "—"}`;
      }
      setResult({ kind: "files", message });
      setPickedFiles([]);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  // URL
  const [url, setUrl] = useState("");
  const [maxPages, setMaxPages] = useState("");
  const submitUrl = async () => {
    if (!url || !agentId) return;
    setBusy("url"); setErr(null); setResult(null);
    try {
      const opts = {};
      if (chunkSize) opts.chunk_size = Number(chunkSize);
      if (chunkOverlap) opts.chunk_overlap = Number(chunkOverlap);
      if (maxPages) opts.max_pages = Number(maxPages);
      const res = await ingestionApi.crawl(agentId, url, opts);
      setResult({ kind: "url", message: `Crawl started: ${url}. Job: ${res?.data?.job_id || res?.job_id || "—"}` });
      setUrl("");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  // Text
  const [textBody, setTextBody] = useState("");
  const [textSource, setTextSource] = useState("");
  const submitText = async () => {
    if (!textBody.trim() || !agentId) return;
    setBusy("text"); setErr(null); setResult(null);
    try {
      const opts = {};
      if (textSource) opts.source = textSource;
      if (chunkSize) opts.chunk_size = Number(chunkSize);
      if (chunkOverlap) opts.chunk_overlap = Number(chunkOverlap);
      const res = await ingestionApi.ingestText(agentId, textBody, opts);
      setResult({ kind: "text", message: `Text ingested. Job: ${res?.data?.job_id || res?.job_id || "—"}` });
      setTextBody(""); setTextSource("");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  if (!agentId) {
    return <div style={{ color: COLORS.textDim }}>Agent not loaded.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {result && <Banner kind="success" onDismiss={() => setResult(null)}>{result.message}</Banner>}
      {err && <Banner kind="error" onDismiss={() => setErr(null)}>{err}</Banner>}

      {/* Optional fields shared across all three sources */}
      <Card title="Chunking (optional)" footer="Leave blank to fall back to agent flow_config → platform default.">
        <Row>
          <Field label="Chunk size">
            <input type="number" value={chunkSize} onChange={e => setChunkSize(e.target.value)}
              placeholder="1500" style={{ ...inputStyle, width: 100 }} />
          </Field>
          <Field label="Chunk overlap">
            <input type="number" value={chunkOverlap} onChange={e => setChunkOverlap(e.target.value)}
              placeholder="300" style={{ ...inputStyle, width: 100 }} />
          </Field>
          <Field label="Classifications" hint='JSON array, e.g. ["financing","real_estate"]'>
            <input value={classifications} onChange={e => setClassifications(e.target.value)}
              placeholder="" style={{ ...inputStyle, width: 240 }} />
          </Field>
        </Row>
      </Card>

      {/* Files */}
      <Card title="Upload files">
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? COLORS.accent : COLORS.border}`,
            borderRadius: 10, padding: "20px 24px",
            background: dragOver ? COLORS.accentSoft : COLORS.bg,
            cursor: "pointer", textAlign: "center",
            transition: "border-color 0.15s, background 0.15s",
          }}>
          <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4 }}>
            {dragOver ? "Drop files to add" : "Drag files here, or click to pick"}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textDim }}>
            PDF · DOCX · PPTX · CSV · XLSX · JSON · Parquet · TXT · ZIP (bulk) — up to 1 GB per file
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.pptx,.txt,.md,.html,.htm,.csv,.tsv,.xlsx,.json,.jsonl,.xml,.yaml,.yml,.parquet,.zip,.eml,.rtf,.mp4,.mov,.webm,.mkv"
            style={{ display: "none" }}
            onChange={e => onPick(e.target.files)} />
        </div>
        {pickedFiles.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {pickedFiles.map((f, i) => (
              <div key={i} style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: "monospace" }}>
                {f.name} <span style={{ color: COLORS.textDim }}>({(f.size / 1024).toFixed(1)} KB)</span>
              </div>
            ))}
            <div>
              <button
                onClick={submitFiles}
                disabled={!!busy || hasMixedZipAndOther || hasLargeFileInBatch}
                style={btnStyle(COLORS.accent, !!busy || hasMixedZipAndOther || hasLargeFileInBatch)}
              >
                {busy === "files"
                  ? (zipFile ? "Extracting…" : largeFile ? "Uploading in chunks…" : "Uploading…")
                  : zipFile
                    ? `Bulk Ingest (${zipFile.name})`
                    : largeFile
                      ? `Chunked Upload (${largeFile.name}, ${(largeFile.size / 1024 / 1024).toFixed(0)} MB)`
                      : `Upload ${pickedFiles.length} file${pickedFiles.length > 1 ? "s" : ""}`}
              </button>
              <button onClick={() => setPickedFiles([])} disabled={!!busy} style={{
                ...btnStyle(), background: "transparent", color: COLORS.textDim,
                border: `1px solid ${COLORS.border}`, marginLeft: 8,
              }}>Clear</button>
            </div>
          </div>
        )}
      </Card>

      {/* URL */}
      <Card title="Crawl a URL">
        <Row>
          <Field label="URL">
            <input value={url} onChange={e => setUrl(e.target.value)}
              placeholder="https://docs.example.com" style={{ ...inputStyle, width: 380 }} />
          </Field>
          <Field label="Max pages" hint="Optional cap">
            <input type="number" value={maxPages} onChange={e => setMaxPages(e.target.value)}
              placeholder="50" style={{ ...inputStyle, width: 100 }} />
          </Field>
        </Row>
        <button onClick={submitUrl} disabled={!url || !!busy} style={btnStyle(COLORS.cyan, !url || !!busy)}>
          {busy === "url" ? "Starting…" : "Start crawl"}
        </button>
      </Card>

      {/* Text */}
      <Card title="Ingest raw text">
        <Field label="Source label (optional)">
          <input value={textSource} onChange={e => setTextSource(e.target.value)}
            placeholder="e.g. internal_doc.txt" style={{ ...inputStyle, width: 340 }} />
        </Field>
        <textarea value={textBody} onChange={e => setTextBody(e.target.value)}
          placeholder="Paste text content to ingest…"
          style={{
            ...inputStyle, width: "100%", minHeight: 120, resize: "vertical",
            fontFamily: "inherit", boxSizing: "border-box",
          }} />
        <button onClick={submitText} disabled={!textBody.trim() || !!busy} style={btnStyle(COLORS.purple, !textBody.trim() || !!busy)}>
          {busy === "text" ? "Ingesting…" : "Ingest text"}
        </button>
      </Card>
    </div>
  );
}
